// 책갈피 탐색기 — 왼쪽 탐색기 아래 (2026-10-02).
//
// 책갈피는 편집기의 `--@ 이름` 주석 줄이다(utils/sqlBookmarks.js). 같은 프로젝트(또는 개별 스크립트)의 **모든 탭**의 책갈피를
// 탭 순서대로 탭별로 묶어 보인다. 누르면 그 탭으로 가서 그 줄로 옮긴다.
//
// 2026-11-03 시험 피드백: 예전에는 편집기에 커서가 있을 때만 저절로 펼쳐지고, 현재 탭의 책갈피는 탭 이름 없이 맨 위에 섰다.
// 이제 — 지금 어느 탭에 있든 **늘 같은 모양**으로 보인다(펼침은 사람이 머리줄로만 바꾼다) · 탭 이름은 현재 탭에도 늘 붙는다
// (현재 탭은 표시만 다르다) · 탭마다 접을 수 있다(한 탭에 책갈피가 많을 때). 접은 상태는 이 브라우저에 남긴다.
// 펼치고 접는 것은 max-height + opacity 로 부드럽게(UI 규칙), reduced-motion 이면 즉시.
import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FiBookmark, FiChevronUp, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import './BookmarkExplorer.css';

const STORE_KEY = 'livesql.bookmarkExplorer';

function loadUi() {
    try {
        const v = JSON.parse(window.localStorage.getItem(STORE_KEY) || '{}');
        return { closed: !!v.closed, folded: Array.isArray(v.folded) ? v.folded : [] };
    } catch (_) {
        return { closed: false, folded: [] };
    }
}

function saveUi(ui) {
    try { window.localStorage.setItem(STORE_KEY, JSON.stringify(ui)); } catch (_) { /* 저장 못 해도 화면은 그대로 */ }
}

export default function BookmarkExplorer({ groups, onJump }) {
    const { t } = useTranslation();
    const [ui, setUi] = useState(loadUi);
    const update = useCallback((fn) => setUi(prev => { const next = fn(prev); saveUi(next); return next; }), []);
    const total = groups.reduce((n, g) => n + g.items.length, 0);
    const open = total > 0 && !ui.closed;
    const folded = new Set(ui.folded);

    const toggleGroup = (fileId) => update(prev => {
        const s = new Set(prev.folded);
        if (s.has(fileId)) s.delete(fileId); else s.add(fileId);
        return { ...prev, folded: [...s] };
    });

    return (
        <div className={`bmx ${open ? 'bmx-open' : ''}`}>
            <button
                type="button"
                className="bmx-head"
                onClick={() => update(prev => ({ ...prev, closed: !prev.closed }))}
                aria-expanded={open}
                title={open ? t('editor:bookmark.collapseHint') : t('editor:bookmark.expandHint')}
            >
                <FiBookmark size={12} />
                <span className="bmx-title">{t('editor:bookmark.title')}</span>
                <span className="bmx-count">{total}</span>
                {open ? <FiChevronDown size={12} /> : <FiChevronUp size={12} />}
            </button>
            <div className="bmx-body" aria-hidden={!open}>
                {total === 0 ? (
                    <div className="bmx-empty">{t('editor:bookmark.empty')}</div>
                ) : groups.map(g => {
                    const isFolded = folded.has(g.fileId);
                    return (
                        <div key={g.fileId} className={`bmx-group ${g.isActive ? 'bmx-group-active' : ''}`}>
                            <button
                                type="button"
                                className="bmx-group-title"
                                tabIndex={open ? 0 : -1}
                                onClick={() => toggleGroup(g.fileId)}
                                aria-expanded={!isFolded}
                                title={isFolded ? t('editor:bookmark.unfoldHint', { name: g.fileName }) : t('editor:bookmark.foldHint', { name: g.fileName })}
                            >
                                {isFolded ? <FiChevronRight size={11} /> : <FiChevronDown size={11} />}
                                <span className="bmx-group-name">{g.fileName}</span>
                                {g.isActive && <span className="bmx-group-cur">{t('editor:bookmark.current')}</span>}
                                <span className="bmx-group-count">{g.items.length}</span>
                            </button>
                            {!isFolded && (
                                <div className="bmx-group-items">
                                    {g.items.map(bm => (
                                        <button
                                            key={`${g.fileId}:${bm.line}`}
                                            type="button"
                                            className="bmx-item"
                                            tabIndex={open ? 0 : -1}
                                            onClick={() => onJump(g, bm)}
                                            title={t('editor:bookmark.jumpHint', { line: bm.line })}
                                        >
                                            <span className="bmx-name">{bm.name || t('editor:bookmark.unnamed')}</span>
                                            <span className="bmx-line">{bm.line}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

BookmarkExplorer.propTypes = {
    /** [{ fileId, fileName, projectId, isActive, items: [{ line, name }] }] — 탭 순서 */
    groups: PropTypes.array.isRequired,
    onJump: PropTypes.func.isRequired,
};
